import React, { createContext, useContext, useState, useEffect } from "react";
import { languages } from "../lib/languages";
import { translations } from "../lib/translations";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    // Try to get language from local storage, default to English
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        const saved = localStorage.getItem("ssa_language");
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error("Failed to parse saved language", e);
            }
        }
        return languages[0]; // Default to English
    });

    // Translation helper function
    const t = (key) => {
        const langCode = currentLanguage.code;
        // Handle dialects (e.g., 'ar-ma' -> use 'ar-ma' or fallback to 'ar')
        let langTranslations = translations[langCode];

        if (!langTranslations) {
            // Fallback for dialects if not explicitly defined
            if (langCode.includes("-")) {
                const baseCode = langCode.split("-")[0];
                langTranslations = translations[baseCode];
            }
        }

        // Final fallback to English
        if (!langTranslations) {
            langTranslations = translations['en'];
        }

        return langTranslations[key] || translations['en'][key] || key;
    };

    // Change language function
    const changeLanguage = (lang) => {
        setCurrentLanguage(lang);
        localStorage.setItem("ssa_language", JSON.stringify(lang));

        // Optional: Update HTML dir attribute for RTL languages (Arabic, Farsi, Urdu, Hebrew)
        const rtlLanguages = ['ar', 'fa', 'ur', 'he'];
        const isRtl = rtlLanguages.some(code => lang.code.startsWith(code));
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        document.documentElement.lang = lang.code;
    };

    // Initialize HTML attributes on mount
    useEffect(() => {
        const rtlLanguages = ['ar', 'fa', 'ur', 'he'];
        const isRtl = rtlLanguages.some(code => currentLanguage.code.startsWith(code));
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        document.documentElement.lang = currentLanguage.code;
    }, [currentLanguage]);

    return (
        <LanguageContext.Provider value={{ currentLanguage, changeLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error("useLanguage must be used within a LanguageProvider");
    }
    return context;
};
